import React from 'react'
import {type ListRenderItemInfo, StyleSheet, View} from 'react-native'
import {type AppBskyFeedDefs} from '@atproto/api'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useQueryClient} from '@tanstack/react-query'

import {cleanError} from '#/lib/strings/errors'
import {truncateAndInvalidate} from '#/state/queries/util'
import {useVideoTimelineQuery} from '#/state/queries/video-timeline'
import {List, type ListMethods} from '#/view/com/util/List'
import {ListFooter, ListMaybePlaceholder} from '#/components/Lists'
import {MainScrollProvider} from '#/view/com/util/MainScrollProvider'
import {LoadLatestBtn} from '#/view/com/util/load-latest/LoadLatestBtn'
import {useHeaderOffset} from '#/components/hooks/useHeaderOffset'
import {logEvent} from '#/lib/statsig/statsig'
import {isIOS, isNative} from '#/platform/detection'
import {useSetMinimalShellMode} from '#/state/shell'
import {useSession} from '#/state/session'
import {VideoPost} from './VideoPost'
import {useAutoScroll} from './AutoScrollContext'

type PostView = AppBskyFeedDefs.PostView

const renderItem = ({item}: ListRenderItemInfo<PostView>) => {
  return <VideoPost post={item} />
}

const keyExtractor = (item: PostView, index: number) => {
  return `${item.uri}-${index}`
}

const initialNumToRender = 12 // 4 rows × 3 columns

export function VideoFeed({testID}: {testID?: string}) {
  const {_} = useLingui()
  const queryClient = useQueryClient()
  const {currentAccount} = useSession()
  const [isPTR, setIsPTR] = React.useState(false)
  const [isScrolledDown, setIsScrolledDown] = React.useState(false)
  const [hasNew, setHasNew] = React.useState(false)
  const setMinimalShellMode = useSetMinimalShellMode()
  const headerOffset = useHeaderOffset()
  const scrollElRef = React.useRef<ListMethods>(null)

  const {isAutoScrolling, scrollSpeed, stopAutoScroll} = useAutoScroll()

  // Auto-scroll effect
  React.useEffect(() => {
    if (!isAutoScrolling || scrollSpeed === 0) return

    // Speed 1-5 maps to ~0.5, 1.0, 1.6, 2.4, 3.2 px/tick at 100fps
    const INTERVAL_MS = 10
    const pxPerTick = scrollSpeed * 0.64

    const interval = setInterval(() => {
      window.scrollBy({top: pxPerTick})
    }, INTERVAL_MS)

    // Stop auto-scroll on user manual scroll
    const handleWheel = () => {
      stopAutoScroll()
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'PageUp' ||
        e.key === 'PageDown' ||
        e.key === 'Home' ||
        e.key === 'End' ||
        e.key === ' '
      ) {
        stopAutoScroll()
      }
    }

    window.addEventListener('wheel', handleWheel)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      clearInterval(interval)
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isAutoScrolling, scrollSpeed, stopAutoScroll])

  const {
    data,
    isFetched,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = useVideoTimelineQuery()

  const posts = React.useMemo(() => {
    return data?.pages.flatMap(page => page.posts) || []
  }, [data])

  const onPressLoadLatest = React.useCallback(() => {
    // Scroll to top
    scrollElRef.current?.scrollToOffset({
      animated: isNative,
      offset: -headerOffset,
    })
    setMinimalShellMode(false)

    // Truncate cache and refetch
    truncateAndInvalidate(queryClient, [
      'video-timeline',
      currentAccount?.did ?? 'guest',
    ])

    setHasNew(false)
    logEvent('feed:refresh', {
      feedType: 'videos',
      feedUrl: 'videos',
      reason: 'load-latest',
    })
  }, [queryClient, headerOffset, setMinimalShellMode, currentAccount?.did])

  const onRefresh = React.useCallback(async () => {
    setIsPTR(true)
    await refetch()
    setIsPTR(false)
    setHasNew(false)
    logEvent('feed:refresh', {
      feedType: 'videos',
      feedUrl: 'videos',
      reason: 'pull-to-refresh',
    })
  }, [refetch])

  const onEndReached = React.useCallback(() => {
    if (isFetchingNextPage || !hasNextPage || error) return
    fetchNextPage()
  }, [isFetchingNextPage, hasNextPage, error, fetchNextPage])

  const scrollToTop = React.useCallback(() => {
    scrollElRef.current?.scrollToOffset({
      animated: true,
      offset: -headerOffset,
    })
    setMinimalShellMode(false)
  }, [headerOffset, setMinimalShellMode])

  return (
    <View testID={testID} style={styles.container}>
      <MainScrollProvider>
        {posts.length < 1 ? (
          <ListMaybePlaceholder
            isLoading={isLoading || !isFetched}
            isError={isError}
            onRetry={refetch}
            emptyType="results"
            emptyMessage={_(msg`No videos found in your timeline.`)}
            errorMessage={cleanError(error)}
          />
        ) : (
          <List
            ref={scrollElRef}
            data={posts}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            refreshing={isPTR}
            onRefresh={onRefresh}
            onEndReached={onEndReached}
            onEndReachedThreshold={4}
            onScrolledDownChange={setIsScrolledDown}
            // @ts-ignore web only -prf
            desktopFixedHeight
            // @ts-ignore web only -prf
            useGridLayout={true}
            ListFooterComponent={
              <ListFooter
                isFetchingNextPage={isFetchingNextPage}
                error={cleanError(error)}
                onRetry={fetchNextPage}
                hasNextPage={hasNextPage}
              />
            }
            initialNumToRender={initialNumToRender}
            windowSize={11}
            maxToRenderPerBatch={isIOS ? 6 : 3} // Render 3 at a time (1 row)
            updateCellsBatchingPeriod={40}
            removeClippedSubviews={true}
          />
        )}
      </MainScrollProvider>

      {/* Load Latest button */}
      {(isScrolledDown || hasNew) && (
        <LoadLatestBtn
          onPress={onPressLoadLatest}
          label={_(msg`Load new videos`)}
          showIndicator={hasNew}
        />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginLeft: 80,
  },
})
